import React, { FC, useLayoutEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { ROUTE } from '~/app/routes';
import { Action } from '~/lib/authz';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { PlatformRolePermissions } from '~/repositories';
import { useSessionStore } from '~/stores/Session';

interface ProtectedProps {
  permission?: Action;
}

export const Protected: FC<ProtectedProps> = ({ permission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthorized, signIn, signOut, authRequired, isSignedIn, setLoading } = useSessionStore();

  // Track the navigation origin to prevent navigation loops (the location may change before the component is replaced)
  const navigatedFromHashRef = useRef<string | undefined>(undefined);
  const stablePermission = useMemo(() => permission, [permission?.permission, permission?.permissionLevel]);

  useLayoutEffect(() => {
    if (isSignedIn() && !authRequired()) {
      signIn().catch(signOut);
    } else {
      setTimeout(() => setLoading(false), 50);
    }
  }, [isSignedIn, authRequired, signIn, signOut, setLoading]);

  // Handle permission checks and navigation - separate from sign-in
  useLayoutEffect(() => {
    const unsubscribe = useSessionStore.subscribe(
      (state) => [state.currentPermission, state.isLoading],
      (state) => {
        const currentPermissionState = state[0] as PlatformRolePermissions | undefined;
        const isLoading = state[1] as boolean | undefined;

        if (isLoading || location.hash === navigatedFromHashRef.current) {
          return;
        }

        if (!currentPermissionState) {
          navigatedFromHashRef.current = location.hash;
          navigate(`${PUBLIC_ROUTE.LOGIN}?redirect=${encodeURIComponent(location.pathname)}`, {
            replace: true,
          });
          return;
        }

        if (stablePermission && !isAuthorized(stablePermission)) {
          navigatedFromHashRef.current = location.hash;
          navigate(ROUTE.ROOT, {
            state: {
              from: location,
              action: 'platformInfoPermissionError',
            },
            replace: true,
          });
        }
      },
      {
        fireImmediately: true,
        equalityFn: shallow,
      }
    );
    return unsubscribe;
  }, [stablePermission, isAuthorized, navigate, location]);

  return !stablePermission || (stablePermission && isAuthorized(stablePermission)) ? <Outlet /> : null;
};
